import { HexColorPicker } from 'react-colorful'
import { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'

function parsePercent(val) {
  if (!val) return 0
  return parseInt(val) || 0
}

function PercentSlider({ label, value, onChange }) {
  const numVal = parsePercent(value)
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <span style={{ fontSize: '0.7rem', color: '#64748b', width: '38px', flexShrink: 0 }}>{label}</span>
      <input
        type="range"
        min="0"
        max="20"
        step="1"
        value={numVal}
        onChange={(e) => onChange(`${e.target.value}%`)}
        style={{ flex: 1, cursor: 'pointer', height: '4px' }}
      />
      <span style={{ fontSize: '0.7rem', color: '#94a3b8', width: '30px', textAlign: 'right', flexShrink: 0 }}>{numVal}%</span>
    </div>
  )
}

function CollapsibleSection({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '0.4rem 0',
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          fontSize: '0.9rem',
          fontWeight: '600',
          color: '#1e293b'
        }}
      >
        {title}
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', paddingTop: '0.5rem' }}>
          {children}
        </div>
      )}
    </div>
  )
}

export default function CommonSettings({ settings, onUpdate }) {
  const [showBgPicker, setShowBgPicker] = useState(false)
  const [showBorderPicker, setShowBorderPicker] = useState(false)
  const [showOverlayPicker, setShowOverlayPicker] = useState(false)

  const updatePadding = (side, value) => {
    onUpdate({
      padding: { ...settings.padding, [side]: value }
    })
  }

  const updateMargin = (side, value) => {
    onUpdate({
      margin: { ...settings.margin, [side]: value }
    })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600' }}>Spacing</h4>

      {/* Padding */}
      <div>
        <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
          Padding
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          <PercentSlider label="Top" value={settings.padding?.top} onChange={(v) => updatePadding('top', v)} />
          <PercentSlider label="Right" value={settings.padding?.right} onChange={(v) => updatePadding('right', v)} />
          <PercentSlider label="Bottom" value={settings.padding?.bottom} onChange={(v) => updatePadding('bottom', v)} />
          <PercentSlider label="Left" value={settings.padding?.left} onChange={(v) => updatePadding('left', v)} />
        </div>
      </div>

      {/* Margin */}
      <div>
        <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
          Margin
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          <PercentSlider label="Top" value={settings.margin?.top} onChange={(v) => updateMargin('top', v)} />
          <PercentSlider label="Right" value={settings.margin?.right} onChange={(v) => updateMargin('right', v)} />
          <PercentSlider label="Bottom" value={settings.margin?.bottom} onChange={(v) => updateMargin('bottom', v)} />
          <PercentSlider label="Left" value={settings.margin?.left} onChange={(v) => updateMargin('left', v)} />
        </div>
      </div>

      <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', marginTop: '0.5rem' }}>Background & Border</h4>

      {/* Background Color */}
      <div style={{ position: 'relative' }}>
        <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
          Background Color
        </label>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <div
            onClick={() => setShowBgPicker(!showBgPicker)}
            style={{
              width: '36px',
              height: '32px',
              backgroundColor: settings.backgroundColor || '#ffffff',
              border: '1px solid var(--color-border)',
              borderRadius: '6px',
              cursor: 'pointer',
              boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.05)'
            }}
          />
          <input
            type="text"
            value={settings.backgroundColor || ''}
            onChange={(e) => onUpdate({ backgroundColor: e.target.value })}
            onBlur={(e) => {
              if (!e.target.value.trim()) {
                onUpdate({ backgroundColor: 'transparent' })
              }
            }}
            placeholder="transparent"
            style={{ flex: 1, fontSize: '0.85rem', padding: '0.4rem' }}
          />
        </div>
        {showBgPicker && (
          <div style={{ position: 'absolute', zIndex: 10, marginTop: '0.5rem' }}>
            <div
              style={{ position: 'fixed', inset: 0 }}
              onClick={() => setShowBgPicker(false)}
            />
            <HexColorPicker
              color={settings.backgroundColor || '#ffffff'}
              onChange={(color) => onUpdate({ backgroundColor: color })}
            />
          </div>
        )}
      </div>

      {/* Background Image Section */}
      <CollapsibleSection title="Background Image">
        <div>
          <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
            Image URL
          </label>
          <input
            type="text"
            value={settings.backgroundImage || ''}
            onChange={(e) => onUpdate({ backgroundImage: e.target.value })}
            placeholder="https://example.com/image.jpg"
            style={{ width: '100%', fontSize: '0.85rem', padding: '0.4rem' }}
          />
        </div>

        {settings.backgroundImage && (
          <>
            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
                Size
              </label>
              <div style={{ display: 'flex', gap: '0.35rem' }}>
                {['cover', 'contain', 'auto'].map(size => (
                  <button
                    key={size}
                    onClick={() => onUpdate({ backgroundSize: size })}
                    style={{
                      flex: 1,
                      padding: '0.35rem',
                      fontSize: '0.7rem',
                      textTransform: 'capitalize',
                      border: `1.5px solid ${(settings.backgroundSize || 'cover') === size ? 'var(--color-primary)' : '#e2e8f0'}`,
                      borderRadius: '6px',
                      background: (settings.backgroundSize || 'cover') === size ? 'rgba(99,102,241,0.06)' : '#fff',
                      color: (settings.backgroundSize || 'cover') === size ? 'var(--color-primary)' : '#475569',
                      cursor: 'pointer'
                    }}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
                Position
              </label>
              <select
                value={settings.backgroundPosition || 'center center'}
                onChange={(e) => onUpdate({ backgroundPosition: e.target.value })}
                style={{ width: '100%', fontSize: '0.85rem', padding: '0.4rem' }}
              >
                <option value="center center">Center</option>
                <option value="top center">Top</option>
                <option value="bottom center">Bottom</option>
                <option value="left center">Left</option>
                <option value="right center">Right</option>
                <option value="top left">Top Left</option>
                <option value="top right">Top Right</option>
                <option value="bottom left">Bottom Left</option>
                <option value="bottom right">Bottom Right</option>
              </select>
            </div>

            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
                Repeat
              </label>
              <div style={{ display: 'flex', gap: '0.35rem' }}>
                {['no-repeat', 'repeat', 'repeat-x', 'repeat-y'].map(r => (
                  <button
                    key={r}
                    onClick={() => onUpdate({ backgroundRepeat: r })}
                    style={{
                      flex: 1,
                      padding: '0.3rem 0.15rem',
                      fontSize: '0.65rem',
                      border: `1.5px solid ${(settings.backgroundRepeat || 'no-repeat') === r ? 'var(--color-primary)' : '#e2e8f0'}`,
                      borderRadius: '6px',
                      background: (settings.backgroundRepeat || 'no-repeat') === r ? 'rgba(99,102,241,0.06)' : '#fff',
                      color: (settings.backgroundRepeat || 'no-repeat') === r ? 'var(--color-primary)' : '#475569',
                      cursor: 'pointer'
                    }}
                  >
                    {r === 'no-repeat' ? 'None' : r === 'repeat' ? 'Both' : r === 'repeat-x' ? 'X' : 'Y'}
                  </button>
                ))}
              </div>
            </div>

            {/* Overlay Color */}
            <div style={{ position: 'relative' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
                Overlay Color
              </label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <div
                  onClick={() => setShowOverlayPicker(!showOverlayPicker)}
                  style={{
                    width: '36px',
                    height: '32px',
                    backgroundColor: settings.overlayColor || 'transparent',
                    border: '1px solid var(--color-border)',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.05)',
                    backgroundImage: !settings.overlayColor || settings.overlayColor === 'transparent' ? 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc)' : 'none',
                    backgroundSize: '8px 8px',
                    backgroundPosition: '0 0, 4px 4px'
                  }}
                />
                <input
                  type="text"
                  value={settings.overlayColor || ''}
                  onChange={(e) => onUpdate({ overlayColor: e.target.value })}
                  placeholder="rgba(0,0,0,0.5)"
                  style={{ flex: 1, fontSize: '0.85rem', padding: '0.4rem' }}
                />
                {settings.overlayColor && (
                  <button
                    onClick={() => onUpdate({ overlayColor: '' })}
                    style={{ border: 'none', background: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '0.75rem' }}
                  >
                    Clear
                  </button>
                )}
              </div>
              {showOverlayPicker && (
                <div style={{ position: 'absolute', zIndex: 10, marginTop: '0.5rem' }}>
                  <div
                    style={{ position: 'fixed', inset: 0 }}
                    onClick={() => setShowOverlayPicker(false)}
                  />
                  <HexColorPicker
                    color={settings.overlayColor || '#000000'}
                    onChange={(color) => onUpdate({ overlayColor: color + '80' })}
                  />
                </div>
              )}
            </div>

            {/* Preview */}
            <div style={{
              height: '60px',
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              backgroundImage: `url(${settings.backgroundImage})`,
              backgroundSize: settings.backgroundSize || 'cover',
              backgroundPosition: settings.backgroundPosition || 'center center',
              backgroundRepeat: settings.backgroundRepeat || 'no-repeat',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {settings.overlayColor && (
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  backgroundColor: settings.overlayColor
                }} />
              )}
            </div>
          </>
        )}
      </CollapsibleSection>

      {/* Link Settings */}
      <CollapsibleSection title="Link">
        <div>
          <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
            URL
          </label>
          <input
            type="text"
            value={settings.linkUrl || ''}
            onChange={(e) => onUpdate({ linkUrl: e.target.value })}
            placeholder="https://example.com"
            style={{ width: '100%', fontSize: '0.85rem', padding: '0.4rem' }}
          />
        </div>
        {settings.linkUrl && (
          <>
            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
                Title
              </label>
              <input
                type="text"
                value={settings.linkTitle || ''}
                onChange={(e) => onUpdate({ linkTitle: e.target.value })}
                placeholder="Link title (tooltip)"
                style={{ width: '100%', fontSize: '0.85rem', padding: '0.4rem' }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={settings.linkNewTab !== false}
                onChange={(e) => onUpdate({ linkNewTab: e.target.checked })}
                id="link-new-tab"
              />
              <label htmlFor="link-new-tab" style={{ fontSize: '0.8rem', color: '#475569' }}>
                Open in new tab
              </label>
            </div>
          </>
        )}
      </CollapsibleSection>

      {/* Border */}
      <div>
        <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
          Border Width
        </label>
        <input
          type="text"
          placeholder="e.g., 1px (default: 0px)"
          value={settings.borderWidth || ''}
          onChange={(e) => onUpdate({ borderWidth: e.target.value })}
          onBlur={(e) => {
            if (!e.target.value.trim()) {
              onUpdate({ borderWidth: '0px' })
            }
          }}
          style={{ width: '100%', fontSize: '0.85rem', padding: '0.4rem' }}
        />
      </div>

      <div style={{ position: 'relative' }}>
        <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
          Border Color
        </label>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <div
            onClick={() => setShowBorderPicker(!showBorderPicker)}
            style={{
              width: '36px',
              height: '32px',
              backgroundColor: settings.borderColor || '#cccccc',
              border: '1px solid var(--color-border)',
              borderRadius: '6px',
              cursor: 'pointer',
              boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.05)'
            }}
          />
          <input
            type="text"
            value={settings.borderColor || ''}
            onChange={(e) => onUpdate({ borderColor: e.target.value })}
            onBlur={(e) => {
              if (!e.target.value.trim()) {
                onUpdate({ borderColor: '#cccccc' })
              }
            }}
            placeholder="#cccccc"
            style={{ flex: 1, fontSize: '0.85rem', padding: '0.4rem' }}
          />
        </div>
        {showBorderPicker && (
          <div style={{ position: 'absolute', zIndex: 10, marginTop: '0.5rem' }}>
            <div
              style={{ position: 'fixed', inset: 0 }}
              onClick={() => setShowBorderPicker(false)}
            />
            <HexColorPicker
              color={settings.borderColor || '#cccccc'}
              onChange={(color) => onUpdate({ borderColor: color })}
            />
          </div>
        )}
      </div>

      <div>
        <label style={{ fontSize: '0.85rem', fontWeight: '500', display: 'block', marginBottom: '0.5rem' }}>
          Border Radius
        </label>
        <input
          type="text"
          placeholder="e.g., 4px (default: 0px)"
          value={settings.borderRadius || ''}
          onChange={(e) => onUpdate({ borderRadius: e.target.value })}
          onBlur={(e) => {
            if (!e.target.value.trim()) {
              onUpdate({ borderRadius: '0px' })
            }
          }}
          style={{ width: '100%', fontSize: '0.85rem', padding: '0.4rem' }}
        />
      </div>
    </div>
  )
}
